import { useState, useEffect, useRef } from 'react';

interface FilterState {
  occasions: string[];
  styles: string[];
  categories: string[];
  priceMin: number;
  priceMax: number;
}

interface FilterSidebarProps {
  filters: FilterState;
  onFilterChange: (filters: FilterState) => void;
  onClearAll: () => void;
}

const OCCASIONS = [
  'Office Wear',
  'Daily Wear',
  'Campus & Active Wear',
  'Date Night',
  'Festive & Event Wear',
  'Gifting Edits',
];

const STYLES = [
  'Korean Minimal',
  'Multi-Layered',
  'Statement/Bold',
  'Heritage-Modern Fusion',
  'Pearl-Accent',
  'Charm/Personalised',
  'Western Edgy',
];

const CATEGORIES = [
  'Chains & Pendants',
  'Earrings',
  'Matching Sets',
];

const PRICE_MIN = 499;
const PRICE_MAX = 1899;

export default function FilterSidebar({ filters, onFilterChange, onClearAll }: FilterSidebarProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [localMin, setLocalMin] = useState(filters.priceMin);
  const [localMax, setLocalMax] = useState(filters.priceMax);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setLocalMin(filters.priceMin);
    setLocalMax(filters.priceMax);
  }, [filters.priceMin, filters.priceMax]);

  const handlePriceChange = (type: 'min' | 'max', value: number) => {
    if (type === 'min') {
      setLocalMin(value);
    } else {
      setLocalMax(value);
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      onFilterChange({
        ...filters,
        priceMin: type === 'min' ? value : localMin,
        priceMax: type === 'max' ? value : localMax,
      });
    }, 400);
  };

  const toggleCheckbox = (
    list: string[],
    value: string,
    filterKey: 'occasions' | 'styles' | 'categories'
  ) => {
    const updated = list.includes(value)
      ? list.filter((v) => v !== value)
      : [...list, value];
    onFilterChange({ ...filters, [filterKey]: updated });
  };

  const hasActiveFilters =
    filters.occasions.length > 0 ||
    filters.styles.length > 0 ||
    filters.categories.length > 0 ||
    filters.priceMin > PRICE_MIN ||
    filters.priceMax < PRICE_MAX;

  const sidebarContent = (
    <div className="space-y-6">
      {/* Clear all */}
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-heading font-semibold text-primary-500 uppercase tracking-wider">
          Filters
        </h2>
        {hasActiveFilters && (
          <button
            onClick={onClearAll}
            className="text-xs font-label text-accent-600 hover:text-accent-500 transition-colors underline cursor-pointer whitespace-nowrap"
          >
            Clear all
          </button>
        )}
      </div>

      {/* Category */}
      <div>
        <h3 className="text-xs font-label font-semibold text-foreground-800 uppercase tracking-wider mb-3">
          Category
        </h3>
        <div className="flex flex-col gap-2">
          {CATEGORIES.map((cat) => (
            <label
              key={cat}
              className="flex items-center gap-2.5 cursor-pointer group"
            >
              <input
                type="checkbox"
                checked={filters.categories.includes(cat)}
                onChange={() => toggleCheckbox(filters.categories, cat, 'categories')}
                className="w-4 h-4 rounded border-background-300 text-accent-500 focus:ring-accent-400/30 cursor-pointer"
              />
              <span className="text-sm font-label text-foreground-600 group-hover:text-foreground-800 transition-colors">
                {cat}
              </span>
            </label>
          ))}
        </div>
      </div>

      {/* Divider */}
      <div className="h-px bg-background-200/70" />

      {/* Occasion */}
      <div>
        <h3 className="text-xs font-label font-semibold text-foreground-800 uppercase tracking-wider mb-3">
          Occasion
        </h3>
        <div className="flex flex-col gap-2 max-h-[200px] overflow-y-auto pr-1">
          {OCCASIONS.map((occ) => (
            <label
              key={occ}
              className="flex items-center gap-2.5 cursor-pointer group"
            >
              <input
                type="checkbox"
                checked={filters.occasions.includes(occ)}
                onChange={() => toggleCheckbox(filters.occasions, occ, 'occasions')}
                className="w-4 h-4 rounded border-background-300 text-accent-500 focus:ring-accent-400/30 cursor-pointer"
              />
              <span className="text-sm font-label text-foreground-600 group-hover:text-foreground-800 transition-colors">
                {occ}
              </span>
            </label>
          ))}
        </div>
      </div>

      {/* Divider */}
      <div className="h-px bg-background-200/70" />

      {/* Style */}
      <div>
        <h3 className="text-xs font-label font-semibold text-foreground-800 uppercase tracking-wider mb-3">
          Style
        </h3>
        <div className="flex flex-col gap-2 max-h-[220px] overflow-y-auto pr-1">
          {STYLES.map((sty) => (
            <label
              key={sty}
              className="flex items-center gap-2.5 cursor-pointer group"
            >
              <input
                type="checkbox"
                checked={filters.styles.includes(sty)}
                onChange={() => toggleCheckbox(filters.styles, sty, 'styles')}
                className="w-4 h-4 rounded border-background-300 text-accent-500 focus:ring-accent-400/30 cursor-pointer"
              />
              <span className="text-sm font-label text-foreground-600 group-hover:text-foreground-800 transition-colors">
                {sty}
              </span>
            </label>
          ))}
        </div>
      </div>

      {/* Divider */}
      <div className="h-px bg-background-200/70" />

      {/* Price Range */}
      <div>
        <h3 className="text-xs font-label font-semibold text-foreground-800 uppercase tracking-wider mb-3">
          Price Range
        </h3>
        <div className="flex items-center gap-3">
          <div className="flex-1 relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-foreground-400 pointer-events-none">
              ₹
            </span>
            <input
              type="number"
              min={PRICE_MIN}
              max={localMax}
              value={localMin}
              onChange={(e) => handlePriceChange('min', parseInt(e.target.value) || PRICE_MIN)}
              className="w-full pl-7 pr-2 py-2 text-xs font-label border border-background-300 rounded-lg bg-background-50 text-foreground-800 focus:outline-none focus:border-accent-400 focus:ring-1 focus:ring-accent-400/30 transition-colors"
            />
          </div>
          <span className="text-xs text-foreground-400">to</span>
          <div className="flex-1 relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-foreground-400 pointer-events-none">
              ₹
            </span>
            <input
              type="number"
              min={localMin}
              max={PRICE_MAX}
              value={localMax}
              onChange={(e) => handlePriceChange('max', parseInt(e.target.value) || PRICE_MAX)}
              className="w-full pl-7 pr-2 py-2 text-xs font-label border border-background-300 rounded-lg bg-background-50 text-foreground-800 focus:outline-none focus:border-accent-400 focus:ring-1 focus:ring-accent-400/30 transition-colors"
            />
          </div>
        </div>
        <p className="text-[10px] text-foreground-400 mt-2 font-label">
          ₹{PRICE_MIN} — ₹{PRICE_MAX}
        </p>
      </div>
    </div>
  );

  return (
    <>
      {/* Mobile filter trigger */}
      <div className="lg:hidden mb-4">
        <button
          onClick={() => setMobileOpen(true)}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-background-50 border border-background-300 rounded-lg text-sm font-label font-medium text-foreground-700 hover:border-accent-400 transition-colors cursor-pointer whitespace-nowrap"
        >
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-equalizer-line text-sm"></i>
          </div>
          Filters
          {hasActiveFilters && (
            <span className="bg-accent-500 text-primary-800 text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center leading-none">
              {filters.occasions.length + filters.styles.length + filters.categories.length + (filters.priceMin > PRICE_MIN || filters.priceMax < PRICE_MAX ? 1 : 0)}
            </span>
          )}
        </button>
      </div>

      {/* Desktop sidebar */}
      <aside className="hidden lg:block w-full">
        {sidebarContent}
      </aside>

      {/* Mobile drawer overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-foreground-950/40 backdrop-blur-sm"
            onClick={() => setMobileOpen(false)}
          />

          {/* Drawer */}
          <div className="absolute left-0 top-0 bottom-0 w-80 max-w-[85vw] bg-background-50 shadow-xl drawer-enter animate-slide-in p-5 overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-sm font-heading font-semibold text-primary-500 uppercase tracking-wider">
                Filters
              </h2>
              <button
                onClick={() => setMobileOpen(false)}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-background-100 transition-colors cursor-pointer"
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-close-line text-lg text-foreground-500"></i>
                </div>
              </button>
            </div>
            {sidebarContent}
          </div>
        </div>
      )}
    </>
  );
}